import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { getClients, setClientWholesaleStatus } from "../../api/adminService";
import { formatDate } from "./constants";

export default function WholesaleClients() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  function loadClients() {
    setLoading(true);
    getClients({
      search: search || undefined,
      filter: filter || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load clients. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, paginationModel]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadClients();
  }

  async function handleToggleWholesale(row) {
    const next = !row.isWholesaleClient;
    await setClientWholesaleStatus(row.userId, next);
    setRows((prev) => prev.map((r) => (r.userId === row.userId ? { ...r, isWholesaleClient: next } : r)));
  }

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "email", headerName: "Email", flex: 1.2, minWidth: 200 },
      { field: "phone", headerName: "Phone", width: 140, valueGetter: (v) => v || "-" },
      { field: "orderCount", headerName: "Orders", width: 90 },
      {
        field: "createdAt",
        headerName: "Joined",
        width: 170,
        valueFormatter: (v) => formatDate(v),
      },
      {
        field: "isWholesaleClient",
        headerName: "Wholesale Client",
        width: 170,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={Boolean(params.value)}
              onChange={() => handleToggleWholesale(params.row)}
              size="small"
              color="secondary"
            />
            <Typography variant="caption" color={params.value ? "secondary.main" : "text.disabled"} fontWeight={600}>
              {params.value ? "Enabled" : "Off"}
            </Typography>
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Box>
      <Stack direction="row" spacing={1.5} sx={{ mb: 3, alignItems: "center" }}>
        <LocalShippingOutlinedIcon sx={{ fontSize: 30, color: "secondary.main" }} />
        <Box>
          <Typography variant="h4">Wholesale Clients</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose which clients can see and buy at wholesale prices on Wholesale-type stores.
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        variant="outlined"
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{ p: 2, mb: 2, borderRadius: 3 }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ alignItems: { xs: "stretch", sm: "center" } }}
        >
          <TextField
            placeholder="Search name, email or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 260, flexGrow: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter</InputLabel>
            <Select label="Filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <MenuItem value="">All clients</MenuItem>
              <MenuItem value="wholesale">Wholesale clients only</MenuItem>
            </Select>
          </FormControl>
          <Button type="submit" variant="outlined" sx={{ borderRadius: 2.5 }}>
            Search
          </Button>
          <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {rowCount.toLocaleString()} {rowCount === 1 ? "client" : "clients"}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.userId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
      </Paper>
    </Box>
  );
}
